import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { cn } from '../../lib/utils.js';
import { Button, LinearSpinner } from './primitives.js';

export interface ModalDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly subtitle?: string;
  readonly icon?: ReactNode;
  readonly variant?: 'default' | 'destructive';
  readonly presentation?: 'center' | 'slide-up';
  readonly maxWidthClassName?: string;
  readonly submitting?: boolean;
  readonly disableEscapeClose?: boolean;
  readonly closeButtonTestId?: string;
  readonly headerActions?: ReactNode;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly role?: 'dialog' | 'alertdialog';
  readonly 'data-testid'?: string;
}

export function ModalDialog({
  open,
  title,
  subtitle,
  icon,
  variant = 'default',
  presentation = 'center',
  maxWidthClassName,
  submitting = false,
  disableEscapeClose = false,
  closeButtonTestId,
  headerActions,
  onClose,
  children,
  footer,
  role = 'dialog',
  'data-testid': dataTestId,
}: ModalDialogProps): ReactElement | null {
  const isSlideUp = presentation === 'slide-up';
  const [closing, setClosing] = useState(false);

  const handleRequestClose = useCallback((): void => {
    const isJsdom = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent ?? '');
    if (!isSlideUp || isJsdom || typeof window === 'undefined') {
      onClose();
      return;
    }
    setClosing((prev) => {
      if (prev) return prev;
      window.setTimeout(() => {
        setClosing(false);
        onClose();
      }, 180);
      return true;
    });
  }, [isSlideUp, onClose]);

  useEffect(() => {
    if (!open || disableEscapeClose || typeof window === 'undefined') return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !submitting) {
        handleRequestClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, disableEscapeClose, submitting, handleRequestClose]);

  if (!open) return null;

  const resolvedMaxWidthClassName = maxWidthClassName ?? (isSlideUp ? 'max-w-none' : 'max-w-2xl');

  const modalContent = (
    <div
      role={role}
      aria-modal="true"
      aria-label={title}
      data-testid={dataTestId}
      data-presentation={presentation}
      onMouseDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget && !submitting && !disableEscapeClose) {
          handleRequestClose();
        }
      }}
      className={cn(
        'fixed inset-0 m-0 flex justify-center bg-black/50 backdrop-blur-[2px]',
        isSlideUp
          ? cn('z-50 items-end pt-8 sm:pt-12', closing ? 'mp-backdrop-exit' : 'mp-backdrop-enter')
          : 'z-50 items-end p-0 sm:items-center sm:p-4',
      )}
    >
      <div
        className={cn(
          'relative flex w-full flex-col overflow-hidden border border-border text-fg shadow-modal',
          isSlideUp
            ? cn(
                'max-h-[92vh] rounded-t-[16px] border-x-0 border-b-0 bg-page sm:max-h-[90vh]',
                closing ? 'mp-sheet-exit' : 'mp-sheet-enter',
              )
            : 'max-h-[92vh] rounded-t-[16px] border-b-0 bg-surface sm:max-h-[90vh] sm:rounded-modal sm:border-b',
          resolvedMaxWidthClassName,
        )}
      >
        <div className={cn('shrink-0', isSlideUp && 'border-b border-border bg-surface')}>
          {isSlideUp && (
            <div aria-hidden="true" className="flex shrink-0 justify-center pt-2.5 pb-0.5">
              <span className="h-1 w-10 rounded-pill bg-border" />
            </div>
          )}
          {submitting && <LinearSpinner className="shrink-0" />}

          <div className="flex shrink-0 items-start justify-between gap-3 px-4 pb-4 pt-4 sm:px-6 sm:pt-5">
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-subtle',
                  variant === 'destructive'
                    ? 'border-status-error-border bg-status-error-bg text-status-error'
                    : 'bg-surface-muted text-fg-secondary',
                )}
              >
                {icon ?? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    {variant === 'destructive' ? (
                      <>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </>
                    ) : (
                      <>
                        <path d="M12 5v14" />
                        <path d="M5 12h14" />
                      </>
                    )}
                  </svg>
                )}
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-[16px] font-bold tracking-[-0.01em] text-fg">
                  {title}
                </h2>
                {subtitle !== undefined && (
                  <p className="mt-0.5 text-[12px] text-fg-secondary">{subtitle}</p>
                )}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              {headerActions}
              <Button
                type="button"
                variant="secondary"
                size="icon"
                aria-label="Close modal"
                data-testid={closeButtonTestId}
                disabled={submitting}
                onClick={handleRequestClose}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </Button>
            </div>
          </div>
        </div>

        <div
          className={cn('flex-1 overflow-y-auto px-4 pb-5 sm:px-6', isSlideUp && 'bg-page pt-4')}
        >
          {children}
        </div>

        {footer !== undefined && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-surface-subtle px-4 py-3.5 sm:px-6">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return modalContent;
}

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly variant?: 'primary' | 'destructive' | 'danger';
  readonly submitting?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly children?: ReactNode;
  readonly 'data-testid'?: string;
  readonly confirmTestId?: string;
  readonly cancelTestId?: string;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'destructive',
  submitting = false,
  onConfirm,
  onCancel,
  children,
  'data-testid': dataTestId,
  confirmTestId,
  cancelTestId,
}: ConfirmDialogProps): ReactElement | null {
  const isDestructive = variant === 'destructive' || variant === 'danger';
  const buttonVariant = isDestructive ? 'destructive' : 'primary';

  return (
    <ModalDialog
      open={open}
      role="alertdialog"
      title={title}
      variant={isDestructive ? 'destructive' : 'default'}
      maxWidthClassName="max-w-lg"
      submitting={submitting}
      onClose={onCancel}
      {...(dataTestId !== undefined ? { 'data-testid': dataTestId } : {})}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            data-testid={cancelTestId}
            onClick={onCancel}
            disabled={submitting}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={buttonVariant}
            size="sm"
            data-testid={confirmTestId}
            onClick={onConfirm}
            disabled={submitting}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p id="confirm-dialog-desc" className="text-[13px] leading-relaxed text-fg-secondary">
          {description}
        </p>
        {children}
      </div>
    </ModalDialog>
  );
}
